import React from "react";

interface Props {
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
  /** Shown next to the page count when the caller knows the row total. */
  total?: number;
}

/**
 * The Prev/Next footer, previously duplicated in every paginated list page.
 *
 * Renders nothing for a single page — the pages that inlined this all showed a
 * dead "Page 1 of 1" with two disabled buttons, which is noise on a phone where
 * vertical space is the scarce thing.
 *
 * The buttons keep their desktop padding; the 44px floor comes from
 * `_touch.scss` below `md` rather than from a size baked in here.
 */
export const Pager: React.FC<Props> = ({ page, totalPages, onPage, total }) => {
  if (totalPages <= 1) return null;

  return (
    <div className="px-5 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-gray-500">
        Page {page} of {totalPages}
        {typeof total === "number" ? ` · ${total} total` : ""}
      </p>
      <div className="flex gap-2">
        <button
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
        >
          Previous
        </button>
        <button
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pager;
